import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Radio, Play, Pause, X, Headphones, ExternalLink } from 'lucide-react';
import { useAudio } from '../contexts/AudioContext';
import { Link } from 'react-router-dom';

const MobileRadioFAB = ({ variant = 'floating' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    isPlaying,
    isLoading,
    currentTrack,
    togglePlay,
    playRadio,
  } = useAudio();

  const isRadioPlaying = isPlaying && currentTrack.type === 'radio';

  const handlePlayRadio = () => {
    if (currentTrack.type !== 'radio') playRadio();
    else togglePlay();
  };

  // Global playback bridge used by homepage and any future Listen Live CTA.
  useEffect(() => {
    const onPlayRadio = () => playRadio();
    window.addEventListener('rastyle:play-radio', onPlayRadio);
    return () => window.removeEventListener('rastyle:play-radio', onPlayRadio);
  }, [playRadio]);

  return (
    <>
      {variant === 'action-bar' ? (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="relative flex min-w-0 flex-col items-center justify-center gap-1 px-1 text-[10px] font-bold uppercase tracking-wide text-zinc-300 transition hover:bg-white/5 hover:text-white"
          data-testid="mobile-action-radio"
          aria-label="Open Rastyle Radio player"
        >
          <Radio className="h-5 w-5 text-rastyle-orange" />
          <span>Live Radio</span>
          <span className="absolute right-[28%] top-2.5 h-2 w-2 rounded-full bg-red-500 ring-2 ring-black" aria-hidden="true" />
        </button>
      ) : (
        <motion.button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-[calc(5rem+env(safe-area-inset-bottom,0px))] right-4 z-50 md:hidden"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 1, type: 'spring', stiffness: 200 }}
          data-testid="mobile-radio-fab"
          aria-label="Open Rastyle Radio player"
        >
          <div className="relative">
            <motion.div className="absolute inset-0 rounded-full bg-primary/30" animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }} transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }} />
            <motion.div className="absolute inset-0 rounded-full bg-primary/20" animate={{ scale: [1, 1.8, 1], opacity: [0.3, 0, 0.3] }} transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut', delay: 0.3 }} />
            <div className="relative w-14 h-14 rounded-full bg-gradient-to-br from-primary to-amber-600 shadow-lg flex items-center justify-center group">
              <Radio className="w-6 h-6 text-black" />
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-black animate-pulse" />
            </div>
            <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs font-bold text-primary whitespace-nowrap">LIVE</span>
          </div>
        </motion.button>
      )}

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsOpen(false)} className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm md:hidden" data-testid="mobile-radio-backdrop" />
            <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} transition={{ type: 'spring', damping: 25, stiffness: 300 }} className="fixed bottom-0 left-0 right-0 z-[90] md:hidden" data-testid="mobile-radio-drawer">
              <div className="bg-gradient-to-b from-zinc-900 to-black rounded-t-3xl border-t border-primary/30 overflow-hidden">
                <div className="flex justify-center py-3"><div className="w-12 h-1.5 bg-white/20 rounded-full" /></div>
                <div className="px-6 pb-8">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center"><Headphones className="w-6 h-6 text-black" /></div>
                      <div>
                        <h3 className="font-heading font-bold text-lg">Rastyle Radio</h3>
                        <p className="text-sm text-muted-foreground flex items-center gap-2"><span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />Live Stream</p>
                      </div>
                    </div>
                    <button onClick={() => setIsOpen(false)} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center" data-testid="close-mobile-drawer" aria-label="Close radio player"><X className="w-5 h-5" /></button>
                  </div>

                  <div className="bg-white/5 rounded-xl p-4 mb-6">
                    <p className="text-xs text-primary font-semibold mb-1">NOW PLAYING</p>
                    <p className="font-semibold">{currentTrack.title}</p>
                    <p className="text-sm text-muted-foreground">{currentTrack.artist}</p>
                  </div>

                  <div className="flex flex-col items-center gap-4">
                    <motion.button onClick={handlePlayRadio} disabled={isLoading} whileTap={{ scale: 0.95 }} className={`w-20 h-20 rounded-full flex items-center justify-center transition-all ${isRadioPlaying ? 'bg-red-500 shadow-[0_0_30px_rgba(239,68,68,0.5)]' : 'bg-primary shadow-[0_0_30px_rgba(212,175,55,0.5)]'}`} data-testid="mobile-play-radio" aria-label={isRadioPlaying ? 'Pause Rastyle Radio' : 'Play Rastyle Radio'}>
                      {isLoading ? <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} className="w-8 h-8 border-3 border-black border-t-transparent rounded-full" /> : isRadioPlaying ? <Pause className="w-10 h-10 text-white" /> : <Play className="w-10 h-10 text-black ml-1" />}
                    </motion.button>
                    <p className="text-sm font-semibold">{isLoading ? 'Connecting...' : isRadioPlaying ? 'Tap to Pause' : 'Tap to Listen Live'}</p>
                  </div>

                  <div className="mt-6 pt-6 border-t border-white/10">
                    <p className="text-xs text-muted-foreground mb-3 text-center">ALSO AVAILABLE ON</p>
                    <div className="flex justify-center gap-4">
                      <a href="https://play.google.com/store/apps/details?id=com.rastyle.rastyleradio&hl=en" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 p-2"><div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">▶</div><span className="text-xs">Play Store</span></a>
                      <a href="https://apps.apple.com/ke/app/rastyle-radio-do-the-reggae/id6446504420" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 p-2"><div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center"></div><span className="text-xs">App Store</span></a>
                      <a href="https://tunein.com/radio/Rastyle-Radio-s250919/" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 p-2"><div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center"><Radio className="w-5 h-5" /></div><span className="text-xs">TuneIn</span></a>
                    </div>
                  </div>

                  <Link to="/radio" onClick={() => setIsOpen(false)} className="mt-4 w-full py-3 bg-white/5 hover:bg-white/10 rounded-xl flex items-center justify-center gap-2 text-sm font-semibold transition-colors" data-testid="mobile-go-to-radio"><Radio className="w-4 h-4" />Go to Radio Page<ExternalLink className="w-3 h-3 ml-1" /></Link>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default MobileRadioFAB;
